import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Autocomplete, Button, CircularProgress, TextField } from "@mui/material";
import { toast } from "react-toastify";
import InputField from "components/Input/InputField";
import TextArea from "components/Input/TextArea";
import { getPublicIntakeFormInfo, submitPublicIntakeForm } from "utils/clientIntakeFormService";

export default function ClientIntakeForm() {
  const { token } = useParams();
  const [formInfo, setFormInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    getPublicIntakeFormInfo(token)
      .then((info) => {
        setFormInfo(info);
        if (info?.alreadySubmitted) setSubmitted(true);
        setLoading(false);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Invalid or expired form link.";
        setError(msg);
        setLoading(false);
      });
  }, [token]);

  const fields = useMemo(() => {
    if (!formInfo?.fieldsJson) return [];
    try {
      return JSON.parse(formInfo.fieldsJson).filter((f) => f.enabled);
    } catch {
      return [];
    }
  }, [formInfo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (fieldId, option, checked) => {
    setForm((prev) => {
      const current = prev[fieldId] || [];
      return {
        ...prev,
        [fieldId]: checked ? [...current, option] : current.filter((v) => v !== option),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const field of fields) {
      if (field.required) {
        const val = form[field.id];
        const empty =
          !val ||
          (Array.isArray(val) && val.length === 0) ||
          (typeof val === "string" && !val.trim());
        if (empty) {
          toast.error(`${field.label} is required`);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      await submitPublicIntakeForm(token, form);
      setSubmitted(true);
      toast.success("Form submitted successfully. Thank you!");
    } catch (err) {
      const msg = err?.response?.data?.errorMessage || "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-app-surface-alt via-app-surface-muted to-app-surface-muted">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-app-surface-alt via-app-surface-muted to-app-surface-muted px-4">
        <div className="bg-app-surface rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl">!</span>
          </div>
          <h2 className="text-xl font-bold text-app-text mb-2">Link Unavailable</h2>
          <p className="text-app-text-muted">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-app-surface-alt via-app-surface-muted to-app-surface-muted px-4">
        <div className="bg-app-surface rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-app-text mb-2">Form Submitted</h2>
          <p className="text-app-text-muted">
            Thank you{formInfo?.clientName ? `, ${formInfo.clientName}` : ""}! Your intake form has
            been received.
          </p>
          <p className="text-app-text-muted text-sm mt-2">You can close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-app-surface-alt via-app-surface-muted to-app-surface-muted flex items-center justify-center py-8 px-4">
      <div className="w-full max-w-5xl bg-app-surface rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative bg-primary px-4 md:px-8 py-4 md:py-8">
          <div className="absolute inset-0 bg-black/10" />
          <div className="relative flex flex-col md:flex-row items-center gap-4">
            {formInfo?.logo && (
              <img
                src={formInfo.logo}
                alt="Logo"
                className="w-24 object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">
                {formInfo?.orgName || "Client Intake Form"}
              </h1>
              {formInfo?.clientName && (
                <p className="text-blue-100 text-sm font-medium">
                  Hello, {formInfo.clientName}. Please complete your intake form below.
                </p>
              )}
            </div>
          </div>
        </div>

        <form className="p-8 space-y-8" onSubmit={handleSubmit}>
          <div className="bg-gradient-to-br from-app-surface-alt to-app-surface-muted rounded-2xl p-4 md:p-8 border border-gray-200/50 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {fields.map((field) => {
                const wrapperClass = field.layout === "full" ? "col-span-1 md:col-span-2" : "";

                if (field.type === "textarea") {
                  return (
                    <div key={field.id} className={wrapperClass}>
                      <TextArea
                        label={field.label}
                        name={field.id}
                        value={form[field.id] || ""}
                        onChange={handleChange}
                        required={field.required}
                      />
                    </div>
                  );
                }

                if (field.type === "checkbox-group") {
                  const opts = field.options || [];
                  return (
                    <div key={field.id} className={wrapperClass}>
                      <label className="block uppercase text-app-text-secondary text-xs font-semibold mb-2">
                        {field.label} {field.required && <sup className="text-red-500">*</sup>}
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                        {opts.map((opt) => (
                          <label key={opt} className="flex items-center gap-3 cursor-pointer group">
                            <input
                              type="checkbox"
                              value={opt}
                              checked={(form[field.id] || []).includes(opt)}
                              onChange={(e) =>
                                handleCheckboxChange(field.id, opt, e.target.checked)
                              }
                              className="text-primary bg-app-surface-muted border-app-border rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <span className="text-app-text font-medium group-hover:text-primary text-sm transition-colors">
                              {opt}
                            </span>
                          </label>
                        ))}
                      </div>
                      {(form[field.id] || []).includes("Other") && (
                        <InputField
                          label="Please specify"
                          name={`${field.id}_other`}
                          value={form[`${field.id}_other`] || ""}
                          onChange={handleChange}
                        />
                      )}
                    </div>
                  );
                }

                if (field.type === "autocomplete") {
                  const opts = (field.options || []).map((o) => ({ label: o, value: o }));
                  const selected = opts.find((o) => o.value === form[field.id]) || null;
                  return (
                    <div key={field.id} className={wrapperClass}>
                      <Autocomplete
                        options={opts}
                        value={selected}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={field.label}
                            required={field.required}
                            size="small"
                          />
                        )}
                        onChange={(_, val) =>
                          handleChange({ target: { name: field.id, value: val?.value || "" } })
                        }
                        isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                      />
                    </div>
                  );
                }

                if (field.type === "select") {
                  const opts = field.options || [];
                  return (
                    <div key={field.id} className={wrapperClass}>
                      <TextField
                        select
                        label={field.label}
                        name={field.id}
                        value={form[field.id] || ""}
                        onChange={handleChange}
                        required={field.required}
                        size="small"
                        fullWidth
                        SelectProps={{ native: true }}
                      >
                        <option value="" />
                        {opts.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </TextField>
                    </div>
                  );
                }

                return (
                  <div key={field.id} className={wrapperClass}>
                    <InputField
                      label={field.label}
                      name={field.id}
                      value={form[field.id] || ""}
                      onChange={handleChange}
                      required={field.required}
                      type={field.type}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
              className="px-8"
            >
              {submitting ? "Submitting..." : "Submit Form"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
